import { useMutation, useQuery } from '@apollo/client/react'
import {
  Button,
  Divider,
  Input,
  notification,
  Popconfirm,
  Table,
  type TableColumnProps,
  Tag,
} from 'antd'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  DeleteUploadDocument,
  FailedDocumentsDocument,
  type FailedDocumentsQuery,
  FailedDocumentType,
  RenameUploadDocument,
} from '~app/graph/generated/graphql'

/*
 * Util
 */

const notify = (type: string, message: string, description: string) => {
  notification[type]({
    message,
    description,
    placement: 'bottomLeft',
  })
}

function formatFilename(filename: string) {
  if (filename.includes('/')) {
    return filename.substr(filename.lastIndexOf('/') + 1)
  }
  return filename
}

const colorFromTagtype = (type: FailedDocumentType) => {
  switch (type) {
    case FailedDocumentType.Rejected:
      return 'orange'
    case FailedDocumentType.DocumentExceedsLimit:
      return 'red'
    case FailedDocumentType.UnSupportedMedia:
      return 'magenta'
    default:
      return '#f50'
  }
}

type FailedDocument = FailedDocumentsQuery['failedDocuments'][0]

/*
 * Component/Route
 */

const FaultyDocuments = () => {
  const { t } = useTranslation()
  // Get faulty documents with query
  const { data, loading, refetch } = useQuery(FailedDocumentsDocument)
  // Rename faulty document with mutation
  const [renameUpload] = useMutation(RenameUploadDocument)
  // Rename faulty document with mutation
  const [deleteUpload] = useMutation(DeleteUploadDocument)

  const [editingRecord, setEditingRecord] = useState<
    FailedDocument | undefined
  >()
  const [editedFilename, setEditedFilename] = useState<string | undefined>()

  async function save(recordToEdit: FailedDocument, newFileName: string) {
    try {
      await renameUpload({
        variables: {
          key: recordToEdit.key,
          newFileName: formatFilename(newFileName),
        },
      })
      notify(
        'success',
        t('faultyDocuments.notifications.filenameChangedTitle'),
        t('faultyDocuments.notifications.filenameChangedDescription')
      )
    } catch (e) {
      notify('error', t('faultyDocuments.notifications.error'), e.message)
    }
    // Refetch data after the name has been changed (or not changed)
    refetch()
    setEditingRecord(undefined)
  }

  const columns: TableColumnProps<FailedDocument>[] = [
    {
      title: t('faultyDocuments.table.filename'),
      render: (_, record) => {
        if (editingRecord && record.key === editingRecord.key) {
          return (
            <Input
              autoFocus
              value={editedFilename}
              onPressEnter={() => {
                if (!editedFilename || editingRecord.key === editedFilename) {
                  notify(
                    'info',
                    t('faultyDocuments.notifications.filenameNotChangedTitle'),
                    t('faultyDocuments.notifications.filenameNotChangedDesc')
                  )
                } else {
                  save(editingRecord, editedFilename)
                }
              }}
              onChange={e => setEditedFilename(e.target.value)}
              defaultValue={record.key}
            />
          )
        }
        return (
          <span
            role="button"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setEditedFilename(record.key)
              setEditingRecord(record)
            }}
          >
            {record.key}
          </span>
        )
      },
    },
    {
      title: t('faultyDocuments.table.type'),
      render: (_, record) => {
        return (
          <Tag color={colorFromTagtype(record.type)} key={record.type}>
            {record.type}
          </Tag>
        )
      },
    },
    {
      title: t('faultyDocuments.table.actions'),
      key: 'actions',
      render: record => (
        <span>
          <a role="button" onClick={() => setEditingRecord(undefined)}>
            {editingRecord && record.key === editingRecord.key
              ? t('faultyDocuments.table.cancelRename')
              : t('faultyDocuments.table.rename')}
          </a>
          <Divider type="vertical" />
          <a href={record.file} target="_blank" rel="noreferrer">
            <Trans
              i18nKey="faultyDocuments.table.openInNewTab"
              values={{ filename: formatFilename(record.key) }}
              // biome-ignore lint/correctness/useJsxKeyInIterable: static single-element array
              components={[<strong />]}
            />
          </a>
          <Divider type="vertical" />
          <Popconfirm
            title={t('misc.confirmationDialog.title')}
            okText={t('misc.confirmationDialog.okText')}
            cancelText={t('misc.confirmationDialog.cancelText')}
            placement="bottom"
            onConfirm={async () => {
              try {
                const result = await deleteUpload({
                  variables: { key: record.key },
                })
                if (result && result.error) {
                  const error = result.error
                  notification.error({
                    message: t('deleteDocuments.errorDeletingDocument'),
                    description: error.message,
                  })
                } else {
                  notification.success({
                    message: t('misc.success'),
                    description: t(
                      'deleteDocuments.documentDeletedSuccessfully'
                    ),
                  })
                }
              } catch (error) {
                notification.error({
                  message: t('deleteDocuments.errorDeletingDocument'),
                  description:
                    error.message || t('deleteDocuments.errorMessageFatal'),
                })
              }
              refetch()
            }}
          >
            <a role="button" onClick={() => setEditingRecord(undefined)}>
              {t('deleteDocuments.removeDocument')}
            </a>
          </Popconfirm>
        </span>
      ),
    },
  ]
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Button type="primary" onClick={() => refetch()}>
          {t('faultyDocuments.refetch')}
        </Button>
      </div>
      <Table
        loading={loading}
        columns={columns}
        dataSource={(data && data.failedDocuments) || []}
        pagination={false}
      />
    </div>
  )
}

export default FaultyDocuments
